import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Calculator, RefreshCw } from 'lucide-react';
import axios from 'axios';

// Extracted Tab Components
import AccountingDashboard from '@/components/accounting/AccountingDashboard';
import AccountingTransactions from '@/components/accounting/AccountingTransactions';
import AccountingInvoices from '@/components/accounting/AccountingInvoices';
import AccountingReports from '@/components/accounting/AccountingReports';
import AccountingTaxSlips from '@/components/accounting/AccountingTaxSlips';
import AccountingBankRecon from '@/components/accounting/AccountingBankRecon';
import ChartOfAccounts from '@/components/accounting/ChartOfAccounts';
import AccountingBills from '@/components/accounting/AccountingBills';
import ShareholderPlanner from '@/components/accounting/ShareholderPlanner';

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('token');

export default function Accounting() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';
  
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [fiscalYear, setFiscalYear] = useState(new Date().getFullYear());
  const [balancesRecalculated, setBalancesRecalculated] = useState(false);
  const [fiscalYears, setFiscalYears] = useState([]);
  
  // Data states
  const [dashboard, setDashboard] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [chartOfAccounts, setChartOfAccounts] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [profitLoss, setProfitLoss] = useState(null);
  const [taxSummary, setTaxSummary] = useState(null);
  const [t4aAgents, setT4aAgents] = useState([]);
  const [allAgents, setAllAgents] = useState([]);
  const [t4aSummary, setT4aSummary] = useState(null);
  const [taxSlipsData, setTaxSlipsData] = useState({ employees: [], contractors: [], summary: {} });
  const [bankTransactions, setBankTransactions] = useState([]);
  const [reconciliationSummary, setReconciliationSummary] = useState(null);
  const [shareholderAllocations, setShareholderAllocations] = useState([]);
  const [bills, setBills] = useState([]);
  const [vendors, setVendors] = useState([]);

  useEffect(() => {
    loadData();
  }, [fiscalYear]);

  const loadData = async () => {
    setLoading(true);
    const token = getToken();
    
    try {
      const [
        dashboardRes, transactionsRes, accountsRes, invoicesRes,
        fiscalYearsRes, profitLossRes, taxSummaryRes,
        t4aAgentsRes, t4aSummaryRes, bankTransRes,
        reconcileSummaryRes, allAgentsRes, billsRes, vendorsRes
      ] = await Promise.all([
        axios.get(`${API}/api/accounting/dashboard?fiscal_year=${fiscalYear}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/accounting/transactions?fiscal_year=${fiscalYear}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/accounting/chart-of-accounts`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/accounting/invoices`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/accounting/reports/fiscal-years`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/accounting/reports/profit-loss?fiscal_year=${fiscalYear}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/accounting/reports/tax-summary?fiscal_year=${fiscalYear}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/accounting/tax-slips/agents?fiscal_year=${fiscalYear}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/accounting/t4a/summary?fiscal_year=${fiscalYear}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/accounting/bank-transactions`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/accounting/reconciliation/summary`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/agents`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/accounting/bills`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/vendors`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
      ]);
      
      setDashboard(dashboardRes.data);
      setTransactions(transactionsRes.data);
      setChartOfAccounts(accountsRes.data);
      setInvoices(invoicesRes.data);
      setFiscalYears(fiscalYearsRes.data.fiscal_years);
      setProfitLoss(profitLossRes.data);
      setTaxSummary(taxSummaryRes.data);
      setT4aAgents(t4aAgentsRes.data.agents || [...(t4aAgentsRes.data.employees || []), ...(t4aAgentsRes.data.contractors || [])]);
      setTaxSlipsData(t4aAgentsRes.data);
      setT4aSummary(t4aSummaryRes.data);
      setBankTransactions(bankTransRes.data || []);
      setReconciliationSummary(reconcileSummaryRes.data);
      setAllAgents(allAgentsRes.data || []);
      setBills(billsRes.data || []);
      setVendors(vendorsRes.data || []);
      
      try {
        const allocRes = await axios.get(
          `${API}/api/accounting/shareholders/allocations?fiscal_year=${fiscalYear}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setShareholderAllocations(allocRes.data || []);
      } catch (e) {
      toast.error(e.response?.data?.detail || "An error occurred");
    }
    } catch (err) {
      toast.error('Failed to load accounting data');
    } finally {
      setLoading(false);
    }
  };

  const syncFromDivisions = async () => {
    const token = getToken();
    try {
      const response = await axios.post(
        `${API}/api/accounting/sync-from-divisions?fiscal_year=${fiscalYear}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      loadData();
    } catch (err) {
      toast.error('Failed to sync data');
    }
  };

  const autoRecalculateBalances = async () => {
    if (balancesRecalculated) return;
    const token = getToken();
    try {
      const response = await axios.post(
        `${API}/api/accounting/chart-of-accounts/recalculate-balances`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBalancesRecalculated(true);
      if (response.data.updated_count > 0) {
        toast.info(`Synced ${response.data.updated_count} account balance(s)`);
        loadData();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "An error occurred");
    }
  };

  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    if (newTab === 'accounts' && isAdmin) {
      autoRecalculateBalances();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="accounting-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <Calculator className="w-8 h-8" />
            Accounting
          </h1>
          <p className="text-slate-500 mt-1">Full accounting module with income, expenses, and reports</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={fiscalYear.toString()} onValueChange={(v) => setFiscalYear(parseInt(v))}>
            <SelectTrigger className="w-32" data-testid="fiscal-year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {fiscalYears.map(year => (
                <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={syncFromDivisions} data-testid="sync-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Sync Data
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="grid w-full grid-cols-9">
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="transactions">Transactions</TabsTrigger>
          <TabsTrigger value="invoices">Invoices</TabsTrigger>
          <TabsTrigger value="bills">Bills</TabsTrigger>
          <TabsTrigger value="reports">Reports</TabsTrigger>
          <TabsTrigger value="shareholder" data-testid="shareholder-tab">Shareholder</TabsTrigger>
          <TabsTrigger value="t4a">T4/T4A</TabsTrigger>
          <TabsTrigger value="reconciliation">Bank Recon</TabsTrigger>
          <TabsTrigger value="accounts">Accounts</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="mt-6 space-y-6">
          <AccountingDashboard
            dashboard={dashboard}
            fiscalYear={fiscalYear}
            navigate={navigate}
            setActiveTab={setActiveTab}
          />
        </TabsContent>

        <TabsContent value="transactions" className="mt-6">
          <AccountingTransactions
            transactions={transactions}
            chartOfAccounts={chartOfAccounts}
            isAdmin={isAdmin}
            fiscalYear={fiscalYear}
            loadData={loadData}
          />
        </TabsContent>

        <TabsContent value="invoices" className="mt-6 space-y-4">
          <AccountingInvoices
            invoices={invoices}
            chartOfAccounts={chartOfAccounts}
            allAgents={allAgents}
            isAdmin={isAdmin}
            loadData={loadData}
          />
        </TabsContent>

        <TabsContent value="bills" className="mt-6 space-y-4">
          <AccountingBills
            bills={bills}
            chartOfAccounts={chartOfAccounts}
            vendors={vendors}
            isAdmin={isAdmin}
            loadData={loadData}
          />
        </TabsContent>

        <TabsContent value="reports" className="mt-6 space-y-6">
          <AccountingReports
            profitLoss={profitLoss}
            taxSummary={taxSummary}
            fiscalYear={fiscalYear}
            shareholderAllocations={shareholderAllocations}
            setActiveTab={setActiveTab}
          />
        </TabsContent>

        <TabsContent value="shareholder" className="mt-6 space-y-6">
          <ShareholderPlanner
            fiscalYear={fiscalYear}
            loadData={loadData}
          />
        </TabsContent>

        <TabsContent value="t4a" className="mt-6 space-y-6">
          <AccountingTaxSlips
            t4aSummary={t4aSummary}
            taxSlipsData={taxSlipsData}
            t4aAgents={t4aAgents}
            fiscalYear={fiscalYear}
            shareholderAllocations={shareholderAllocations}
            loadData={loadData}
          />
        </TabsContent>

        <TabsContent value="reconciliation" className="mt-6 space-y-6">
          <AccountingBankRecon
            reconciliationSummary={reconciliationSummary}
            bankTransactions={bankTransactions}
            transactions={transactions}
            loadData={loadData}
          />
        </TabsContent>

        <TabsContent value="accounts" className="mt-6">
          <ChartOfAccounts
            isAdmin={isAdmin}
            chartOfAccounts={chartOfAccounts}
            fiscalYear={fiscalYear}
            loadData={loadData}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
